import { useState, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multiselect';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  PoundSterling,
  TrendingUp,
  AlertTriangle,
  Calendar,
  ChevronLeft,
  ChevronRight,
  X,
  ExternalLink,
  Clock,
  CheckCircle2,
  Mail,
  MessageSquare,
  FileText,
  Download,
  Search,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useNavigate } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import { useTableSort } from '@/hooks/useTableSort';

// Mock data for multi-company dashboard
const mockCompanyData = [
  {
    id: '1',
    name: 'Acme Corporation',
    totalAR: 450000,
    overdue: 125000,
    overduePercent: 27.8,
    dso: 45,
    cei: 87,
    avgDaysToPay: 42,
    arTrend: '+12%',
    overdueTrend: '+8%',
    dsoTrend: '-3 days',
    ceiTrend: '+5%',
    paymentSpeed: 'Improving',
  },
  {
    id: '2',
    name: 'Global Industries Ltd',
    totalAR: 680000,
    overdue: 95000,
    overduePercent: 14.0,
    dso: 38,
    cei: 92,
    avgDaysToPay: 35,
    arTrend: '+5%',
    overdueTrend: '-2%',
    dsoTrend: '-5 days',
    ceiTrend: '+3%',
    paymentSpeed: 'Improving',
  },
  {
    id: '3',
    name: 'Tech Innovations Inc',
    totalAR: 320000,
    overdue: 180000,
    overduePercent: 56.3,
    dso: 72,
    cei: 65,
    avgDaysToPay: 68,
    arTrend: '+18%',
    overdueTrend: '+25%',
    dsoTrend: '+12 days',
    ceiTrend: '-8%',
    paymentSpeed: 'Declining',
  },
  {
    id: '4',
    name: 'Retail Solutions Co',
    totalAR: 540000,
    overdue: 75000,
    overduePercent: 13.9,
    dso: 32,
    cei: 94,
    avgDaysToPay: 30,
    arTrend: '+3%',
    overdueTrend: '-5%',
    dsoTrend: '-2 days',
    ceiTrend: '+2%',
    paymentSpeed: 'Stable',
  },
  {
    id: '5',
    name: 'Manufacturing Plus',
    totalAR: 890000,
    overdue: 220000,
    overduePercent: 24.7,
    dso: 52,
    cei: 78,
    avgDaysToPay: 49,
    arTrend: '+15%',
    overdueTrend: '+18%',
    dsoTrend: '+8 days',
    ceiTrend: '-4%',
    paymentSpeed: 'Declining',
  },
  {
    id: '6',
    name: 'Services Group',
    totalAR: 410000,
    overdue: 45000,
    overduePercent: 11.0,
    dso: 28,
    cei: 96,
    avgDaysToPay: 26,
    arTrend: '+2%',
    overdueTrend: '-3%',
    dsoTrend: '-4 days',
    ceiTrend: '+1%',
    paymentSpeed: 'Improving',
  },
  {
    id: '7',
    name: 'Distribution Network',
    totalAR: 750000,
    overdue: 165000,
    overduePercent: 22.0,
    dso: 48,
    cei: 82,
    avgDaysToPay: 45,
    arTrend: '+10%',
    overdueTrend: '+12%',
    dsoTrend: '+6 days',
    ceiTrend: '-3%',
    paymentSpeed: 'Declining',
  },
];

// Mock 30-day trend data
const mock30DayTrend = [
  { day: 'Day 1', dso: 48, cei: 85 },
  { day: 'Day 5', dso: 50, cei: 83 },
  { day: 'Day 10', dso: 52, cei: 81 },
  { day: 'Day 15', dso: 49, cei: 84 },
  { day: 'Day 20', dso: 47, cei: 86 },
  { day: 'Day 25', dso: 45, cei: 88 },
  { day: 'Day 30', dso: 45, cei: 87 },
];

// Mock top customers data
const mockTopCustomers = [
  { name: 'TechFlow Industries', outstanding: 12450, daysOverdue: 45 },
  { name: 'Summit Enterprises', outstanding: 8920, daysOverdue: 32 },
  { name: 'Horizon Solutions Ltd', outstanding: 6780, daysOverdue: 28 },
  { name: 'Apex Manufacturing', outstanding: 5340, daysOverdue: 18 },
  { name: 'Quantum Logistics', outstanding: 4200, daysOverdue: 12 },
];

// Mock recent activity
const mockRecentActivity = [
  { type: 'payment', customer: 'Customer A', timestamp: '2 hours ago', icon: CheckCircle2, color: 'text-green-600' },
  { type: 'promise', customer: 'Customer B', timestamp: '5 hours ago', icon: Clock, color: 'text-blue-600' },
  { type: 'email', customer: 'Customer C', timestamp: '1 day ago', icon: Mail, color: 'text-purple-600' },
  { type: 'dispute', customer: 'Customer D', timestamp: '2 days ago', icon: MessageSquare, color: 'text-orange-600' },
];

const ITEMS_PER_PAGE = 7;

type TrendPeriod = 'latest' | '7days' | '30days' | '60days' | '90days';

export default function MultiCompanyDashboard() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<TrendPeriod>('latest');
  const [selectedCompany, setSelectedCompany] = useState<typeof mockCompanyData[0] | null>(null);
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // Calculate aggregated metrics
  const totalOutstanding = mockCompanyData.reduce((sum, company) => sum + company.totalAR, 0);
  const totalOverdue = mockCompanyData.reduce((sum, company) => sum + company.overdue, 0);
  const avgDSO = Math.round(mockCompanyData.reduce((sum, company) => sum + company.dso, 0) / mockCompanyData.length);

  // Find most at-risk company
  const mostAtRiskCompany = mockCompanyData.reduce((max, company) => 
    company.overduePercent > max.overduePercent ? company : max
  );

  // Company options for multi-select
  const companyOptions = mockCompanyData.map(company => ({
    value: company.id,
    label: company.name,
  }));

  // Filter data based on selected companies and search query
  const filteredData = useMemo(() => {
    let data = mockCompanyData;

    // Filter by selected companies
    if (selectedCompanyIds.length > 0) {
      data = data.filter(company => selectedCompanyIds.includes(company.id));
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      data = data.filter(company => 
        company.name.toLowerCase().includes(query)
      );
    }

    return data;
  }, [selectedCompanyIds, searchQuery]);

  // Sorting
  const { sortedData, requestSort, getSortDirection } = useTableSort(filteredData);

  // Pagination
  const totalPages = Math.ceil(sortedData.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedData = sortedData.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useMemo(() => {
    setCurrentPage(1);
  }, [selectedCompanyIds, searchQuery]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getOverdueColor = (percent: number) => {
    if (percent >= 30) return 'text-red-600';
    if (percent >= 20) return 'text-orange-600';
    return 'text-green-600';
  };

  const getDSOColor = (dso: number) => {
    if (dso >= 60) return 'text-red-600';
    if (dso >= 45) return 'text-orange-600';
    return 'text-green-600';
  };

  const getCEIColor = (cei: number) => {
    if (cei >= 90) return 'text-green-600';
    if (cei >= 75) return 'text-orange-600';
    return 'text-red-600';
  };

  const getTrendPeriodLabel = (period: TrendPeriod) => {
    const labels: Record<TrendPeriod, string> = {
      latest: 'Latest',
      '7days': '7 Days',
      '30days': '30 Days',
      '60days': '60 Days',
      '90days': '90 Days',
    };
    return labels[period];
  };

  const exportToExcel = () => {
    // Prepare data for export
    const dataToExport = filteredData.map(company => ({
      Company: company.name,
      'Total AR': formatCurrency(company.totalAR),
      'Overdue': formatCurrency(company.overdue),
      'Overdue %': `${company.overduePercent.toFixed(1)}%`,
      'DSO': company.dso,
      'CEI': `${company.cei}%`,
      'Avg Days to Pay': company.avgDaysToPay,
      'AR Trend': company.arTrend,
      'Overdue Trend': company.overdueTrend,
      'DSO Trend': company.dsoTrend,
      'CEI Trend': company.ceiTrend,
      'Payment Speed': company.paymentSpeed,
    }));

    // Create CSV content
    const headers = Object.keys(dataToExport[0] || {});
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(row => 
        headers.map(header => {
          const value = row[header as keyof typeof row];
          // Escape quotes and wrap in quotes if contains comma
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    // Create blob and download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `company-performance-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Multi-Company Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Consolidated view across all companies in your portfolio
        </p>
      </div>

      {/* Top Section - Key Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Outstanding */}
        <Card className="p-6 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Total Outstanding</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalOutstanding)}</p>
              <div className="flex items-center gap-1 text-xs text-green-700">
                <TrendingUp className="h-3 w-3" />
                <span>+8% vs last month</span>
              </div>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <PoundSterling className="h-6 w-6 text-teal-700" />
            </div>
          </div>
        </Card>

        {/* Total Overdue */}
        <Card className="p-6 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Total Overdue</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalOverdue)}</p>
              <div className="flex items-center gap-1 text-xs text-orange-700">
                <TrendingUp className="h-3 w-3" />
                <span>+12% vs last month</span>
              </div>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <TrendingUp className="h-6 w-6 text-teal-700" />
            </div>
          </div>
        </Card>

        {/* Most At-Risk Company */}
        <Card className="p-6 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Most At-Risk Company</p>
              <p className="text-lg font-bold text-gray-900 truncate">{mostAtRiskCompany.name}</p>
              <Badge variant="destructive" className="text-xs">
                {mostAtRiskCompany.overduePercent.toFixed(1)}% overdue
              </Badge>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-teal-700" />
            </div>
          </div>
        </Card>

        {/* Avg DSO */}
        <Card className="p-6 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb]">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <p className="text-sm font-medium text-gray-700">Avg DSO (Last 30 Days)</p>
              <p className="text-3xl font-bold text-gray-900">{avgDSO} days</p>
              <div className="flex items-center gap-1 text-xs text-green-700">
                <TrendingUp className="h-3 w-3 rotate-180" />
                <span>-2 days improvement</span>
              </div>
            </div>
            <div className="p-3 bg-white/50 rounded-lg">
              <Calendar className="h-6 w-6 text-teal-700" />
            </div>
          </div>
        </Card>
      </div>

      {/* Bottom Section - Company Performance Comparison Table */}
      <Card className="p-6">
        <div className="space-y-4">
          {/* Table Header with Search and Filters */}
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-semibold whitespace-nowrap">Company Performance Comparison</h2>
            <div className="flex items-center gap-3 flex-1">
              <div className="flex-1 max-w-xs">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search companies..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <MultiSelect
                  options={companyOptions}
                  placeholder="Filter by companies..."
                  emptyMessage="No companies found"
                  value={selectedCompanyIds}
                  onValueChange={setSelectedCompanyIds}
                  maxDisplay={2}
                />
              </div>
              <Select value={viewMode} onValueChange={(v) => setViewMode(v as TrendPeriod)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="latest">Latest</SelectItem>
                  <SelectItem value="7days">7 Days</SelectItem>
                  <SelectItem value="30days">30 Days</SelectItem>
                  <SelectItem value="60days">60 Days</SelectItem>
                  <SelectItem value="90days">90 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="icon"
                onClick={exportToExcel}
                title="Export to Excel"
                disabled={filteredData.length === 0}
              >
                <Download className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <SortableTableHead
                      sortKey="name"
                      currentSortDirection={getSortDirection('name')}
                      onSort={() => requestSort('name')}
                      align="left"
                    >
                      Company
                    </SortableTableHead>
                    {viewMode === 'latest' ? (
                      <>
                        <SortableTableHead
                          sortKey="totalAR"
                          currentSortDirection={getSortDirection('totalAR')}
                          onSort={() => requestSort('totalAR')}
                          align="right"
                        >
                          Total AR
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overdue"
                          currentSortDirection={getSortDirection('overdue')}
                          onSort={() => requestSort('overdue')}
                          align="right"
                        >
                          Overdue
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overduePercent"
                          currentSortDirection={getSortDirection('overduePercent')}
                          onSort={() => requestSort('overduePercent')}
                          align="right"
                        >
                          Overdue %
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="dso"
                          currentSortDirection={getSortDirection('dso')}
                          onSort={() => requestSort('dso')}
                          align="right"
                        >
                          DSO
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="cei"
                          currentSortDirection={getSortDirection('cei')}
                          onSort={() => requestSort('cei')}
                          align="right"
                        >
                          CEI
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="avgDaysToPay"
                          currentSortDirection={getSortDirection('avgDaysToPay')}
                          onSort={() => requestSort('avgDaysToPay')}
                          align="right"
                        >
                          Avg Days to Pay
                        </SortableTableHead>
                      </>
                    ) : (
                      <>
                        <SortableTableHead
                          sortKey="arTrend"
                          currentSortDirection={getSortDirection('arTrend')}
                          onSort={() => requestSort('arTrend')}
                          align="right"
                        >
                          AR Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="overdueTrend"
                          currentSortDirection={getSortDirection('overdueTrend')}
                          onSort={() => requestSort('overdueTrend')}
                          align="right"
                        >
                          Overdue Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="dsoTrend"
                          currentSortDirection={getSortDirection('dsoTrend')}
                          onSort={() => requestSort('dsoTrend')}
                          align="right"
                        >
                          DSO Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="ceiTrend"
                          currentSortDirection={getSortDirection('ceiTrend')}
                          onSort={() => requestSort('ceiTrend')}
                          align="right"
                        >
                          CEI Trend
                        </SortableTableHead>
                        <SortableTableHead
                          sortKey="paymentSpeed"
                          currentSortDirection={getSortDirection('paymentSpeed')}
                          onSort={() => requestSort('paymentSpeed')}
                          align="right"
                        >
                          Payment Speed
                        </SortableTableHead>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan={viewMode === 'latest' ? 7 : 6} className="p-8 text-center text-muted-foreground">
                        No companies found matching your filters
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((company) => (
                    <tr
                      key={company.id}
                      className="border-t hover:bg-muted/50 cursor-pointer transition-colors"
                      onClick={() => setSelectedCompany(company)}
                    >
                      <td className="p-4 font-medium">{company.name}</td>
                      {viewMode === 'latest' ? (
                        <>
                          <td className="text-right p-4">{formatCurrency(company.totalAR)}</td>
                          <td className="text-right p-4">{formatCurrency(company.overdue)}</td>
                          <td className={cn("text-right p-4 font-medium", getOverdueColor(company.overduePercent))}>
                            {company.overduePercent.toFixed(1)}%
                          </td>
                          <td className={cn("text-right p-4 font-medium", getDSOColor(company.dso))}>
                            {company.dso}
                          </td>
                          <td className={cn("text-right p-4 font-medium", getCEIColor(company.cei))}>
                            {company.cei}%
                          </td>
                          <td className="text-right p-4">{company.avgDaysToPay}</td>
                        </>
                      ) : (
                        <>
                          <td className="text-right p-4 text-green-600 font-medium">{company.arTrend}</td>
                          <td className="text-right p-4 text-orange-600 font-medium">{company.overdueTrend}</td>
                          <td className="text-right p-4 text-green-600 font-medium">{company.dsoTrend}</td>
                          <td className="text-right p-4 text-green-600 font-medium">{company.ceiTrend}</td>
                          <td className="text-right p-4">
                            <Badge
                              variant={
                                company.paymentSpeed === 'Improving'
                                  ? 'default'
                                  : company.paymentSpeed === 'Declining'
                                  ? 'destructive'
                                  : 'secondary'
                              }
                            >
                              {company.paymentSpeed}
                            </Badge>
                          </td>
                        </>
                      )}
                    </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div>
              {sortedData.length === 0 ? (
                'No companies found'
              ) : (
                <>
                  Showing {startIndex + 1}-{Math.min(endIndex, sortedData.length)} of {sortedData.length} companies
                  {(selectedCompanyIds.length > 0 || searchQuery) && (
                    <span className="ml-1">(filtered from {mockCompanyData.length} total)</span>
                  )}
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Company Detail Side Panel */}
      {selectedCompany && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end">
          <div className="w-[600px] h-full bg-background shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
            {/* Header */}
            <div className="sticky top-0 bg-background border-b p-6 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold">{selectedCompany.name}</h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSelectedCompany(null)}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {/* Key Metrics */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Key Metrics</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-4 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb] border-none">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700">Total Outstanding</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(selectedCompany.totalAR)}</p>
                      </div>
                      <PoundSterling className="h-5 w-5 text-teal-700" />
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb] border-none">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700">Total Overdue</p>
                        <p className="text-2xl font-bold text-red-700">{formatCurrency(selectedCompany.overdue)}</p>
                      </div>
                      <TrendingUp className="h-5 w-5 text-red-700" />
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb] border-none">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700">Avg DSO</p>
                        <p className="text-2xl font-bold text-gray-900">{selectedCompany.dso} days</p>
                      </div>
                      <Calendar className="h-5 w-5 text-teal-700" />
                    </div>
                  </Card>

                  <Card className="p-4 bg-gradient-to-br from-[#e0f2f1] to-[#b2dfdb] border-none">
                    <div className="flex items-start justify-between">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-700">CEI</p>
                        <p className="text-2xl font-bold text-gray-900">{selectedCompany.cei}%</p>
                      </div>
                      <CheckCircle2 className="h-5 w-5 text-green-700" />
                    </div>
                  </Card>
                </div>
              </div>

              {/* 30-Day Performance Trend */}
              <div>
                <h3 className="text-lg font-semibold mb-4">30-Day Performance Trend</h3>
                <Card className="p-4">
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={mock30DayTrend}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="day" stroke="#6b7280" fontSize={12} />
                      <YAxis stroke="#6b7280" fontSize={12} />
                      <RechartsTooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                        }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="dso"
                        stroke="#00897B"
                        strokeWidth={2}
                        name="DSO"
                        dot={{ fill: '#00897B', r: 4 }}
                        animationDuration={1500}
                      />
                      <Line
                        type="monotone"
                        dataKey="cei"
                        stroke="#2196F3"
                        strokeWidth={2}
                        name="CEI"
                        dot={{ fill: '#2196F3', r: 4 }}
                        animationDuration={1500}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </div>

              {/* Today's Priorities */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Today's Priorities</h3>
                <div className="space-y-3">
                  {mockTopCustomers.slice(0, 3).map((customer, index) => (
                    <Card key={index} className="p-4 border-l-4 border-l-teal-500 bg-white hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{customer.name}</p>
                          <p className="text-sm text-red-600 mt-1 font-medium">{formatCurrency(customer.outstanding)} overdue by {customer.daysOverdue} days</p>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Recent Activity */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Recent Activity</h3>
                <Card className="p-4">
                  <div className="space-y-4">
                    {mockRecentActivity.map((activity, index) => {
                      const Icon = activity.icon;
                      return (
                        <div key={index} className="flex items-start gap-3">
                          <div className={cn("p-2 rounded-full bg-muted", activity.color)}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-medium capitalize">{activity.type}</p>
                            <p className="text-sm text-muted-foreground">{activity.customer}</p>
                          </div>
                          <p className="text-xs text-muted-foreground">{activity.timestamp}</p>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>

              {/* Top Customers by Outstanding */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Top Customers by Outstanding</h3>
                <Card className="p-4">
                  <div className="space-y-2">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-sm font-medium">Customer</th>
                          <th className="text-right py-2 text-sm font-medium">Outstanding</th>
                          <th className="text-right py-2 text-sm font-medium">Days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mockTopCustomers.map((customer, index) => (
                          <tr key={index} className="border-b last:border-0">
                            <td className="py-2 text-sm">{customer.name}</td>
                            <td className="text-right py-2 text-sm font-medium">
                              {formatCurrency(customer.outstanding)}
                            </td>
                            <td className="text-right py-2 text-sm text-muted-foreground">
                              {customer.daysOverdue}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>

              {/* Action Button */}
              <Button className="w-full" size="lg" onClick={() => navigate('/')}>
                <ExternalLink className="h-4 w-4 mr-2" />
                View Full Company Dashboard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
